/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react';
import {
  faLaptop,
  faMobile,
  faBrowser,
} from '@fortawesome/pro-regular-svg-icons';

import { Button } from '@client/components/common/inputs/Button';
import { useModal } from '@client/components/common/Modal';
import Spinner from '@client/components/Spinner';

import {
  useLandingPageConfigQuery,
  LandingPageConfigQuery,
  useUpdateLandingPageConfigMutation,
  useAddLandingPageConfigMutation,
  BundlePricing,
  BundleStatus,
  BundleItemType,
} from '../../../../generated/graphql';
import { PreviewModal, TPreviewType } from './Modals';
import { FAQs } from './Faqs';
import { Reviews } from './Reviews';
import { TextSection2 } from './TextSection2';
import { Container } from './container/Container';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Testimonials } from './Testimonials';
import { upload } from '@client/components/ajax';
import { IntroductionForm } from './Introduction';
import { IEmailCaptureSection } from '@playbook-technologies/common-schemas';
import { ISection, OfferData, SubOfferData } from './types';
import { ExtendedLandingPagesBuilderSchema } from './schema';
import { TextSection1 } from './TextSection1';
import { defaultFaqs, removeAlpha } from './helpers';
import { BioSection } from './BioSection';
import { ProgramsCarousel } from './ProgramsCarousel';
import { FeaturesSection } from './FeaturesSection';
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider, UseFormRegisterReturn } from 'react-hook-form';
import { loadPageConfig } from './defaultConfig';
import { PrimaryButton } from '@client/new/components/button';
import { muiForm } from '@client/new/helpers/forms';
import { toast } from 'react-hot-toast';
import { BrandingForm } from './Branding';
import { ProgramsHighlight } from './ProgramsHighlight';
import { FeaturedBenefit } from './FeaturedBenefit';
import { HeroFullVideoForm } from './HeroFullVideo';
import { DEFAULT_LOGOS, LogosForm } from './Logos';
import { TextSection } from './TextSection';
import { HeroFullImageForm } from './HeroFullImage';
import { DEFAULT_FEATURES, FeaturesNewSection } from './FeaturesNewSection';
import { TMap } from '@client/new/helpers/types';
import { useSections } from './hooks';
import { CustomDomainBanner } from '@client/new/components/CustomDomainBanner';
import { EmailCaptureForm } from './EmailCapture';
import { useInfluencerAssetsUploader, UploadConfig } from './helpers/upload';
import { useRouterWillLeave } from '@client/new/hooks/useRouterWillLeave';
import { isEmpty } from 'lodash';
import { RoutingProvider } from '@client/new/hooks/useRouting';
import { FeaturedOffer } from './FeaturedOffer';
import { OffersCarousel } from './OffersCarousel';
import { Errors } from '@client/new/pages/promotions/view/components/Errors';
import { toErrors } from './mappers';
import { trackEvent } from '@services/tracking';
import { SubscriptionCarousel } from './SubscriptionCarousel';

type SectionsTitles = TMap<ISection['type'], string>;
const SECTION_TITLES: SectionsTitles = {
  Bio: 'Bio',
  TextSection: 'Headline + text',
  TextSection1: 'Headline + text + media',
  TextSection2: 'Headline + text + 2 images',
  PaidProgramsList: 'Paid programs carousel',
  ProgramsList: 'Subscription programs carousel',
  ProgramsHighlight: 'Featured program',
  Features: 'Features',
  FeaturedBenefit: 'Featured benefit',
  HeroFullImage: 'Hero image',
  FeaturesNew: 'App features',
  HeroFullVideo: 'Hero video',
  FAQs: 'FAQs',
  Reviews: 'Reviews',
  Testimonials: 'Testimonials',
  Introduction: 'Hero with benefits list',
  Logos: 'Publishers',
  EmailCapture: 'Email Capture',
  FeaturedOffer: 'Featured offer',
  OffersCarousel: 'Offers carousel',
  SubscriptionCarousel: 'Subscription content carousel',
};

const CreatorContext =
  React.createContext<LandingPageConfigQuery['creator']>(null);

export function useOptionalCreator() {
  return React.useContext(CreatorContext);
}

export function useCreator() {
  const context = useOptionalCreator();

  if (!context) {
    throw new Error('useCreator must be used within a CreatorProvider');
  }

  return context;
}

function useDataLoader(influencerId: number) {
  const {
    data: queryData,
    loading: isLoading,
    error: landingPageConfigError,
    refetch: refetchLandingPageConfig,
  } = useLandingPageConfigQuery({
    variables: {
      influencerId,
      creatorId: String(influencerId), // Content query requires ID! (string)
      // Fetch every published program so PaidProgramsList has data even when no active bundle
      forActiveBundle: false,
    },
    fetchPolicy: 'network-only',
  });

  const loading = isLoading;
  const error = landingPageConfigError;

  const backendConfig = queryData?.landingPageConfig ?? {
    config: null,
    id: null,
  };

  const bundles = queryData?.bundle || [];
  const otpOffers = bundles.filter((b) => b.pricing === BundlePricing.Otp);
  const subOffer = bundles.find(
    (b) =>
      b.pricing === BundlePricing.Subscription &&
      !b.isPlatformBundle &&
      b.status !== BundleStatus.Archived,
  );

  return {
    backendConfig,
    creator: queryData?.creator,
    otpOffers,
    subOffer,
    feature: queryData?.feature,
    content: queryData?.content,
    error,
    loading,
  };
}

function DataLoader(props: {
  children: any;
  params: { influencerId: string };
}) {
  const influencerId = Number(props.params.influencerId);

  const data = useDataLoader(influencerId);

  if (data.loading) {
    return <Spinner />;
  }

  if (data.error) {
    return <div>Error</div>;
  }

  return props.children({
    ...props,
    ...data,
  });
}

type Props = ReturnType<typeof useDataLoader> & {
  params: { influencerId: string };
  route: any;
  router: any;
};
export function LandingPageBuilder(props: Props) {
  /**
   * NOTE: this ensures that all data loaded in DataLoader is available
   * before the page is rendered (initialState can be set based on data)
   */

  return (
    <RoutingProvider routing={{ router: props.router, route: props.route }}>
      <DataLoader {...props}>
        {(props: Props) => <Page {...props} />}
      </DataLoader>
    </RoutingProvider>
  );
}

export function Page(props: Props) {
  const influencerId = Number(props.params.influencerId);

  const { universalUpload, isUploading } =
    useInfluencerAssetsUploader(influencerId);
  const s3ImageUploadUncompressed = async (
    file: File,
    config?: UploadConfig,
  ) => {
    const { cdn01, image }: any = await upload(file, influencerId, {
      skipCompression: true,
      onProgress: config?.onProgress,
    });
    return cdn01 + image;
  };

  const { creator, backendConfig, otpOffers, subOffer, feature, content } =
    props;

  const serverConfig = backendConfig?.config;

  // Map bundles to OfferData with all bundle items (programs, content, features)
  const mapBundleToOfferData = (
    bundle: typeof props.otpOffers,
    features: typeof props.feature,
    content: typeof props.content,
    programs: typeof props.creator.publishedPrograms,
  ): OfferData[] => {
    if (!bundle || bundle.length === 0) return [];

    // Flatten content items from the nested structure
    const contentItems =
      content?.contentItems?.flatMap((item) => {
        const value = item.value;
        if (value && 'id' in value && 'title' in value) {
          return [{ id: value.id, title: value.title }];
        }
        return [];
      }) || [];

    return bundle
      .filter((b) => b.status !== BundleStatus.Archived)
      .map((b) => {
        const bundleItems = b.items || [];

        // Extract PROGRAM items
        const programItemIds = bundleItems
          .filter((item) => item.itemType === 'PROGRAM')
          .map((item) => item.itemId)
          .filter((id): id is number => id !== null && id !== undefined);

        const programOptions = programs
          .filter((p) => programItemIds.includes(p.id))
          .map((p) => ({
            value: String(p.id),
            label: p.name,
          }));

        // Extract CONTENT items
        const contentItemIds = bundleItems
          .filter((item) => item.itemType === 'CONTENT')
          .map((item) => item.contentId)
          .filter((id): id is string => id !== null && id !== undefined);

        const contentOptions = contentItems
          .filter((c) => contentItemIds.includes(c.id))
          .map((c) => ({
            value: String(c.id),
            label: c.title,
          }));

        // Extract FEATURE items
        const featureItemIds = bundleItems
          .filter((item) => item.itemType === 'FEATURE')
          .map((item) => item.itemId)
          .filter((id): id is number => id !== null && id !== undefined);

        const featureOptions = features
          .filter((f) => featureItemIds.includes(f.id))
          .map((f) => ({
            value: f.code,
            label: f.name,
          }));

        // Combine all items in order: programs → content → features
        const allItemOptions = [
          ...programOptions,
          ...contentOptions,
          ...featureOptions,
        ];

        return {
          id: b.id,
          name: b.title,
          price: b.oneTimeProduct?.price,
          checkoutUrl: b.checkoutPage?.url,
          landingPageUrl: b.landingPageUrl || undefined,
          listedItems:
            allItemOptions.length > 0 ? { options: allItemOptions } : undefined,
        };
      });
  };

  const mapBundleToSubscriptionData = (
    bundle: typeof props.subOffer,
    features: typeof props.feature,
    content: typeof props.content,
    programs: typeof props.creator.publishedPrograms,
  ): SubOfferData | null => {
    if (isEmpty(bundle)) return null;

    // Flatten content items from the nested structure
    const contentItems =
      content?.contentItems?.flatMap((item) => {
        const value = item.value;
        if (value && 'id' in value && 'title' in value) {
          return [{ id: value.id, title: value.title }];
        }
        return [];
      }) || [];

    const bundleItems = bundle.items || [];

    // Extract PROGRAM items
    const programItemIds = bundleItems
      .filter((item) => item.itemType === BundleItemType.Program)
      .map((item) => item.itemId)
      .filter((id): id is number => id !== null && id !== undefined);

    const programOptions = programs
      .filter((p) => programItemIds.includes(p.id))
      .map((p) => ({
        value: p.id,
        label: p.name,
      }));

    // Extract CONTENT items
    const contentItemIds = bundleItems
      .filter((item) => item.itemType === BundleItemType.Content)
      .map((item) => item.contentId)
      .filter((id): id is string => id !== null && id !== undefined);

    const contentOptions = contentItems
      .filter((c) => contentItemIds.includes(c.id))
      .map((c) => ({
        value: String(c.id),
        label: c.title,
      }));

    // Extract FEATURE items
    const featureItemIds = bundleItems
      .filter((item) => item.itemType === BundleItemType.Feature)
      .map((item) => item.itemId)
      .filter((id): id is number => id !== null && id !== undefined);

    const featureOptions = features
      .filter((f) => featureItemIds.includes(f.id))
      .map((f) => ({
        value: f.code,
        label: f.name,
      }));

    return {
      id: bundle.id,
      name: bundle.title,
      price: bundle.oneTimeProduct?.price,
      checkoutUrl: bundle.checkoutPage?.url,
      landingPageUrl: bundle.landingPageUrl || undefined,
      programs: programOptions,
      content: contentOptions,
      features: featureOptions,
    };
  };

  const otpOffersData = mapBundleToOfferData(
    otpOffers,
    feature,
    content,
    creator?.publishedPrograms || [],
  );

  const subOfferData = mapBundleToSubscriptionData(
    subOffer,
    feature,
    content,
    creator?.publishedPrograms || [],
  );

  const [primaryColor, setPrimaryColor] = useState(
    serverConfig
      ? serverConfig?.branding.primaryColor
      : '#' +
          (creator?.customColorSchema?.primaryColor || '6367EF').toUpperCase(),
  );

  const [isSaving, setIsSaving] = useState(false);
  const isEditing = Boolean(backendConfig?.id);

  const colors = useMemo(
    () => [primaryColor, removeAlpha(primaryColor, 0.1), '#FFFFFF'],
    [primaryColor],
  );

  const formMethods = muiForm(
    useForm({
      resolver: zodResolver(ExtendedLandingPagesBuilderSchema),
      defaultValues: loadPageConfig(
        { creator, bundle: subOfferData },
        serverConfig,
        {
          colors,
          primaryColor,
        },
      ) as any,
    }),
  );

  const { handleSubmit, setValue, watch, formState, trigger } = formMethods;

  const isDirty = !isEmpty(formState.dirtyFields);

  useRouterWillLeave(isDirty || isUploading, {
    message: isUploading
      ? 'An upload is currently in progress. Leaving the page will cancel the upload.'
      : 'You have unsaved changes. Are you sure you want to leave without saving?',
  });

  // Track "Landing Page Edited" when user enters the builder
  useEffect(() => {
    if (creator) {
      trackEvent({
        type: 'Landing Page Edited',
        payload: { type: 'subscription' },
      });
    }
  }, [creator]);

  const brandingPrimary = watch('branding.primaryColor');
  useEffect(
    function broadcastPrimaryColorFromIntro() {
      setPrimaryColor(brandingPrimary);
      const sections = watch('sections');
      const updatedSections = sections.map((s: ISection, i: number) => {
        return {
          ...s,
          bgColor: colors[(s.colorIndex as number) ?? 2],
        };
      });
      setValue('sections', updatedSections);
    },
    [brandingPrimary, setValue, watch, colors],
  );

  const beforeSave = (sections: ISection[]) => {
    const updatedSections = sections.map((s: ISection) => {
      if (s.type === 'Logos') {
        s.logos = s.logos.filter((l) => l.url);
      }

      return s;
    });

    return updatedSections;
  };

  const submit = handleSubmit((values) => {
    setIsSaving(true);
    upsertMutation()
      .then(() => {
        toast.success('Page saved successfully!');
        formMethods.reset(values);
        trackEvent({
          type: 'Landing Page Saved',
          payload: { type: 'subscription' },
        });
      })
      .catch((e) => {
        toast.error(`Error during save: ${String(e)}`, {
          duration: 10000,
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
  });

  const onSave = async (e: React.BaseSyntheticEvent<any>) => {
    const sections = watch('sections');
    setValue('sections', beforeSave(sections));

    // Trigger validation on all fields
    const isValid = await trigger();

    if (!isValid) {
      toast.error('Please fix validation errors before saving', {
        duration: 5000,
      });
      return;
    }

    return submit(e);
  };

  const addSection = (index: number) => (sectionType: ISection['type']) => {
    const sections = watch('sections');
    const newSections = [...sections];
    let newSection;
    trackEvent({ type: 'Section Added', payload: { type: 'subscription' } });
    if (sectionType === 'FAQs') {
      newSection = {
        enabled: true,
        type: 'FAQs',
        title: 'Frequently asked questions',
        faqs: defaultFaqs,
        bgColor: colors[2],
        colorIndex: 2,
      };
    } else if (sectionType === 'EmailCapture') {
      newSection = {
        type: sectionType,
        enabled: true,
        bgColor: colors[2],
        colorIndex: 2,
        title: '',
        description: '',
        displayAsPopup: true,
      } as IEmailCaptureSection;
    } else if (sectionType === 'FeaturedOffer') {
      newSection = {
        type: 'FeaturedOffer',
        enabled: true,
        bgColor: colors[2],
        colorIndex: 2,
        offerId: undefined as any, // Will trigger validation error
        headlineText: '', // Will trigger validation error (min 1)
        description: '',
        listedItems: [],
        coverImage: '', // Will trigger validation error (min 1)
        cta1: { enabled: false, text: '', link: '' },
        cta2: { enabled: false, text: '', link: '' },
      };
    } else if (sectionType === 'OffersCarousel') {
      newSection = {
        type: 'OffersCarousel',
        enabled: true,
        bgColor: colors[2],
        colorIndex: 2,
        title: '',
        subtitle: '',
        offerIds: otpOffersData.map((o) => o.id),
      };
    } else if (sectionType === 'SubscriptionCarousel') {
      newSection = {
        type: 'SubscriptionCarousel',
        enabled: !!subOfferData?.id,
        bgColor: colors[2],
        colorIndex: 2,
        title: '',
        subtitle: '',
        offerId: subOfferData?.id,
        programIds: [],
        extrasIds: [],
        communityFeatures: [],
      };
    } else {
      newSection = {
        // NOTE: might require a case-by-case update in future
        type: sectionType,
        enabled: true,
        bgColor: colors[2],
        colorIndex: 2,
        bullets: [],
        title: '',
        desc: '',
        introVideo: null,
        coverImage: '',
        features: DEFAULT_FEATURES,
        logos: DEFAULT_LOGOS,
      } as ISection;
    }
    newSections.splice(index + 1, 0, newSection);
    setValue('sections', newSections, { shouldDirty: true });
  };

  const sectionMethods = useSections(formMethods);

  const deleteSectionWithTracking = (index: number) => {
    return () => {
      trackEvent({
        type: 'Section removed',
        payload: { type: 'subscription' },
      });
      sectionMethods.deleteSection(index)();
    };
  };

  const registerInSection = (index: number) => (path: string) => {
    return formMethods.register(`sections.${index}.${path}`);
  };

  type SectionProps = {
    value: any;
    onChange: (value: unknown) => void;
    register: (path: string) => UseFormRegisterReturn;
    index: number;
  };

  const FORMS = {
    Introduction: (props: SectionProps) => (
      <IntroductionForm
        {...props}
        imageUpload={universalUpload}
        videoUpload={universalUpload}
      />
    ),
    HeroFullVideo: (props: SectionProps) => (
      <HeroFullVideoForm {...props} videoUpload={universalUpload} />
    ),
    HeroFullImage: (props: SectionProps) => (
      <HeroFullImageForm {...props} imageUpload={s3ImageUploadUncompressed} />
    ),
    Logos: (props: SectionProps) => (
      <LogosForm {...props} imageUpload={universalUpload} />
    ),
    TextSection: (props: SectionProps) => <TextSection {...props} />,
    TextSection1: (props: SectionProps) => (
      <TextSection1
        {...props}
        imageUpload={universalUpload}
        videoUpload={universalUpload}
      />
    ),
    ProgramsHighlight: (props: SectionProps) => (
      <ProgramsHighlight
        {...props}
        programs={creator?.publishedPrograms as any[]}
        influencerId={influencerId}
      />
    ),
    FeaturedBenefit: (props: SectionProps) => (
      <FeaturedBenefit {...props} imageUpload={universalUpload} />
    ),
    ProgramsList: (props: SectionProps) => (
      <ProgramsCarousel
        {...props}
        programs={
          (creator?.publishedPrograms.filter(
            (p: any) => p.accessType === 'premium',
          ) || []) as any[]
        }
        influencerId={influencerId}
      />
    ),
    PaidProgramsList: (props: SectionProps) => (
      <ProgramsCarousel
        {...props}
        programs={
          (creator?.publishedPrograms.filter(
            (p: any) => p.accessType === 'exclusive',
          ) || []) as any[]
        }
        influencerId={influencerId}
      />
    ),
    Reviews: (props: SectionProps) => <Reviews {...props} />,
    Features: (props: SectionProps) => (
      // NOTE: deprecateed
      <FeaturesSection {...props} imageUpload={universalUpload} />
    ),
    FeaturesNew: (props: SectionProps) => (
      <FeaturesNewSection {...props} imageUpload={universalUpload} />
    ),
    Testimonials: (props: SectionProps) => (
      <Testimonials {...props} upload={universalUpload} />
    ),
    Bio: (props: SectionProps) => (
      <BioSection {...props} imageUpload={universalUpload} />
    ),
    TextSection2: (props: SectionProps) => (
      <TextSection2 {...props} upload={universalUpload} />
    ),
    FAQs: (props: SectionProps) => <FAQs {...props} />,
    EmailCapture: (props: SectionProps) => (
      <EmailCaptureForm {...props} imageUpload={universalUpload} />
    ),
    FeaturedOffer: (props: SectionProps) => (
      <FeaturedOffer
        {...props}
        offers={otpOffersData}
        imageUpload={universalUpload}
      />
    ),
    OffersCarousel: (props: SectionProps) => (
      <OffersCarousel {...props} offers={otpOffersData} />
    ),
    SubscriptionCarousel: (props: SectionProps) => (
      <SubscriptionCarousel {...props} offer={subOfferData} />
    ),
  };

  const [preview, setPreview] = useState<TPreviewType>(TPreviewType.desktop);

  const previewModal: any = useModal(PreviewModal);

  const state = watch();

  const updateMutation = useUpdateLandingPageConfigMutation({
    variables: {
      data: {
        id: props.backendConfig?.id as number,
        influencerId,
        config: state,
      },
    },
  });

  const createMutation = useAddLandingPageConfigMutation({
    variables: {
      data: {
        influencerId,
        config: state,
      },
    },
  });

  const [upsertMutation] = isEditing ? updateMutation : createMutation;

  const showPreview = (type: TPreviewType) => {
    setPreview(type);
    previewModal.open();
    trackEvent({ type: 'Preview', payload: { type: 'subscription' } });
  };

  if (!creator) return null; // EARLY RETURN;

  const openSalesPage = () =>
    window.open(
      import.meta.env.VITE_LANDING_PAGE_URL + '/' + creator.customURL,
      '_blank',
    );

  return (
    <CreatorContext.Provider value={props.creator}>
      <FormProvider {...formMethods}>
        <div className="landing-page-builder text-gray-1">
          {/* {errors && <div className="text-red-500 whitespace-pre">{errors}</div>} */}

          <>
            <div className="">
              <div className="px-2 border-b border-[#999] lg:sticky top-0 pt-2 bg-white pb-2 z-10 flex flex-wrap justify-center items-center mb-4 sm:justify-between">
                <div className="flex flex-wrap items-center">
                  {isEditing && (
                    <Button
                      className="mr-0 mb-0 w-[100%] sm:w-auto sm:mr-5 mt-3 sm:mt-0"
                      onClick={openSalesPage}
                      variant="gray"
                    >
                      <FontAwesomeIcon
                        icon={faBrowser as any}
                        className="leading-none mr-[13px] ml-[-3px]"
                      />
                      Landing Page
                      <FontAwesomeIcon className="ml-2" icon={faExternalLink} />
                    </Button>
                  )}
                  <div
                    className={
                      'mb-0 mr-5 flex items-center font-bold text-gray-2'
                    }
                  >
                    Preview:
                  </div>
                  <Button
                    className="mr-0 mb-0 w-[100%] sm:w-auto sm:mr-5 mt-3 sm:mt-0 hidden md:block"
                    onClick={() => showPreview(TPreviewType.desktop)}
                    variant="gray"
                  >
                    <FontAwesomeIcon
                      icon={faLaptop as any}
                      className="leading-none mr-[13px] ml-[-3px]"
                    />
                    Desktop
                  </Button>
                  <Button
                    className="mr-0 mb-0 w-[100%] sm:w-auto sm:mr-5 mt-3 sm:mt-0"
                    onClick={() => showPreview(TPreviewType.mobile)}
                    variant="gray"
                  >
                    <FontAwesomeIcon
                      icon={faMobile as any}
                      className="leading-none mr-[13px] ml-[-3px]"
                    />
                    Mobile
                  </Button>
                  <div className="border-r border-[#DBDEE6] hidden sm:block mr-5 h-[36px]" />
                  <PrimaryButton
                    onClick={onSave}
                    isLoading={isSaving}
                    className="w-full md:w-auto mt-3 md:mt-0 h-[44px]"
                  >
                    Save
                  </PrimaryButton>

                  {isDirty ? (
                    <div className="text-center ml-2 text-red-500 font-semibold">
                      You have unsaved changes!
                    </div>
                  ) : null}
                </div>
              </div>
              <CustomDomainBanner influencerId={influencerId} />
              <div className="mb-6">
                <Errors data={toErrors(state.sections, formState.errors)} />
              </div>
              <Container
                title={'Your brand'}
                enabled={true}
                colors={colors}
                color={'#FFF'}
                alwaysEnabled
                colorSelectEnabled={false}
              >
                <BrandingForm imageUpload={universalUpload} />
              </Container>
              {state.sections.map((section: ISection, index: number) => {
                const sectionUpdate = sectionMethods.handleSectionChange(index);
                const register = registerInSection(index);

                return (
                  <Container
                    id={`sections.${index}`}
                    onAddSection={addSection(index)}
                    onDeleteSection={
                      index > 0 ? deleteSectionWithTracking(index) : undefined
                    }
                    onDuplicateSection={
                      index > 0
                        ? sectionMethods.duplicateSection(index)
                        : undefined
                    }
                    onSwitchHeader={
                      index === 0 ? sectionMethods.handleHeroSwitch : undefined
                    }
                    key={index}
                    title={
                      (SECTION_TITLES as SectionsTitles)[section.type] ||
                      section.type
                    }
                    enabled={section.enabled}
                    onChangeEnabled={(enabled) => sectionUpdate({ enabled })}
                    colors={colors}
                    color={colors[(section.colorIndex as number) ?? 2]}
                    onColorIndexChange={(colorIndex) => {
                      sectionUpdate({
                        colorIndex,
                        bgColor: colors[colorIndex],
                      });
                    }}
                    alwaysEnabled={index === 0}
                    colorSelectEnabled
                    onMove={sectionMethods.handleSectionMove(index)}
                    canMoveUp={index > 1}
                    canMoveDown={index > 0 && index < state.sections.length - 1}
                    sectionType={section.type}
                  >
                    {FORMS[section.type]?.({
                      value: section,
                      onChange: sectionUpdate,
                      register,
                      index,
                    })}
                  </Container>
                );
              })}
            </div>

            <previewModal.Popup
              data={state}
              previewUrl={
                import.meta.env.VITE_LANDING_PAGE_URL +
                `/preview/${influencerId}`
              }
              previewType={preview}
            />
          </>
        </div>
      </FormProvider>
    </CreatorContext.Provider>
  );
}
